import type { WorkbookRunMode, WorkbookRunStatus } from "@erp/shared-types";

/** What choosing a Worksheet's Result needs from a listed Run. */
export interface ResultRunRecord {
  status: WorkbookRunStatus;
  /** A Sampled run's Results are never a Worksheet's Shown result. Absent reads as a full Run. */
  mode?: WorkbookRunMode;
  results: readonly { sheetId: string; fingerprint: string }[];
}

type ResultOf<Run extends ResultRunRecord> = Run["results"][number];

/** A Worksheet's Result as every surface shows it: which Run's, and whether it is Current. */
export interface ShownResult<Run extends ResultRunRecord> {
  /** The newest Current Result's Run, else the newest completed Run with this Worksheet, else the newest completed Run. */
  run: Run | undefined;
  result: ResultOf<Run> | undefined;
  /** A Current Result can be exported and built on; a Stale one needs a rerun. */
  current: boolean;
}

function resultIn<Run extends ResultRunRecord>(
  run: Run,
  sheetId: string | null,
): ResultOf<Run> | undefined {
  return run.results.find((result) => result.sheetId === sheetId);
}

/**
 * The newest Current Result of `sheetId` across `runs` (newest first): a
 * completed Run's Result whose Fingerprint equals the Worksheet's in
 * `fingerprints`, the Definition being shown (unsaved edits included in the
 * editor, the saved Definition on the server). Partial Runs combine, and
 * undoing an edit brings an earlier Result back. A Sampled run's Results
 * never count: they were computed over a subset (see `sampleResult`).
 */
export function currentResult<Run extends ResultRunRecord>(
  runs: readonly Run[],
  fingerprints: ReadonlyMap<string, string>,
  sheetId: string | null,
): { run: Run; result: ResultOf<Run> } | undefined {
  const expected = sheetId ? fingerprints.get(sheetId) : undefined;
  if (expected === undefined) return undefined;
  for (const run of runs) {
    if (!isFullResultRun(run)) continue;
    const result = resultIn(run, sheetId);
    if (result?.fingerprint === expected) return { run, result };
  }
  return undefined;
}

/** A completed Run whose Results were computed over the full data: every mode but `sample`. */
function isFullResultRun(run: ResultRunRecord): boolean {
  return run.status === "completed" && run.mode !== "sample";
}

/**
 * The Result of `sheetId` a Sampled run computed, to show instead of the
 * Shown result: the newest completed Sampled run's, while it is newer than
 * every full Run with a Result for it and its Fingerprint is Current.
 * Labelled a sample result wherever it is shown; nothing exports, reports
 * or acts on it.
 */
export function sampleResult<Run extends ResultRunRecord>(
  runs: readonly Run[],
  fingerprints: ReadonlyMap<string, string>,
  sheetId: string | null,
): { run: Run; result: ResultOf<Run> } | undefined {
  const expected = sheetId ? fingerprints.get(sheetId) : undefined;
  if (expected === undefined) return undefined;
  for (const run of runs) {
    if (run.status !== "completed") continue;
    const result = resultIn(run, sheetId);
    if (!result) continue;
    if (run.mode !== "sample") return undefined;
    if (result.fingerprint === expected) return { run, result };
  }
  return undefined;
}

/**
 * The Result a Worksheet shows: its newest Current Result, else its newest
 * Stale one, else none (from the newest completed Run, for its notices).
 */
export function shownResult<Run extends ResultRunRecord>(
  runs: readonly Run[],
  fingerprints: ReadonlyMap<string, string>,
  sheetId: string | null,
): ShownResult<Run> {
  const current = currentResult(runs, fingerprints, sheetId);
  if (current) return { ...current, current: true };
  const completed = runs.filter(isFullResultRun);
  const run =
    completed.find((item) => resultIn(item, sheetId) !== undefined) ??
    completed[0];
  return {
    run,
    result: run && resultIn(run, sheetId),
    current: false,
  };
}
